import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { deltaPercentPoints, formatCompactCurrency, formatShortDate } from '../../../lib/format';
import { type BreakdownScope, useTrend } from '../data/api';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { Delta } from '../shared/Delta';
import { ErrorState } from '../shared/ErrorState';
import { MediaCard } from '../shared/MediaCard';
import { NoData } from '../shared/NoData';
import { TrendChart } from './TrendChart';

function lastNonNull(values: (number | null)[]): number | null {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value != null) return value;
  }
  return null;
}

function StatBlock({
  value,
  label,
  children,
}: {
  value: string;
  label: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color: 'text.primary' }}>
          {value}
        </Typography>
        {children}
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
}

export function TrendCard({
  kpi,
  scope,
  enabled,
}: {
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}): ReactNode {
  const { data, isLoading, isError } = useTrend(scope, enabled);
  const symbol = data?.currencySymbol ?? '';
  const points = data?.points ?? [];
  const values = points.map((point) => point.kpiValue);
  const spendValues = points.map((point) => point.spend);
  const definedValues = values.filter((value): value is number => value !== null);
  const dates = points.map((point) => point.date);

  const deltaPercent = deltaPercentPoints(data?.delta ?? null) ?? 0;
  const spendDeltaPercent = deltaPercentPoints(data?.spendDelta ?? null) ?? 0;
  const onTrack = kpi.higherIsBetter ? deltaPercent >= 0 : deltaPercent <= 0;
  const headlineValue = lastNonNull(values);
  const totalSpend = spendValues.reduce((sum, spend) => sum + spend, 0);
  const formatKpi = (value: number): string => formatKpiValue(value, kpi, symbol);
  const formatSpend = (value: number): string => formatCompactCurrency(value, symbol);

  return (
    <MediaCard sx={{ p: 4, mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
            {`${kpi.label} Trend`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            Tracked over the selected period
          </Typography>
        </Box>
      </Box>

      {isError ? (
        <ErrorState />
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      ) : definedValues.length === 0 ? (
        <NoData message="No trend data for the selected filters." />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 6 }}>
            <StatBlock
              value={formatKpiValue(headlineValue, kpi, symbol)}
              label={`Avg ${kpi.label}`}
            >
              <Delta value={deltaPercent} invert={!kpi.higherIsBetter} fontSize={11} />
            </StatBlock>
            <StatBlock value={formatCompactCurrency(totalSpend, symbol)} label="Total Spend">
              <Delta value={spendDeltaPercent} fontSize={11} />
            </StatBlock>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              my: 1,
              fontWeight: 600,
              color: onTrack ? 'success.main' : 'warning.dark',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: onTrack ? 'success.main' : 'warning.main',
              }}
            />
            {onTrack ? 'On track' : 'Needs attention'}
          </Box>

          <TrendChart
            xAxisData={dates}
            xAxisTickFormatter={(date) =>
              typeof date === 'string' ? formatShortDate(date) : String(date)
            }
            series={[
              { id: 'kpi', label: kpi.label, values, valueFormatter: formatKpi },
              { id: 'spend', label: 'Spend', values: spendValues, valueFormatter: formatSpend },
            ]}
          />
        </>
      )}
    </MediaCard>
  );
}
